import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, LinearProgress, Typography, Collapse, Tooltip } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import dayjs from "dayjs";
import Flag from "react-world-flags";
import SidePanel, { SidePanelSection, SidePanelKV } from "./SidePanel";
import { TiresiaKnowsSection } from "./TiresiaInsight";
import API from "../api";
import { colors } from "../theme";
import { buildPcapngMulti, downloadBlob } from "../utils/pcap";

// ── helpers ─────────────────────────────────────────────────────────────────
const fmtTs = (v) => {
  if (!v) return null;
  const d = dayjs(v?.$date ?? v);
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm:ss") : null;
};


const isEmpty = (v) =>
  v == null || v === "" ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

// Fields handled by dedicated sections → excluded from "Other"
const HANDLED = new Set([
  "id", "_id", "backup", "disabled", "suppressed", "supp",
  "timestamp", "timeStamp",
  "signature", "signature_id", "classificazione", "tipoAttacco", "tipo",
  "severity", "severityLabel", "severityColor",
  "src_ip", "dest_ip", "src_port", "dest_port", "src_mac", "dest_mac",
  "ipSource", "ipDestination", "ipAttaccante", "ipDestinatario",
  "hostNameAtt", "hostNameDest", "customNameSrc", "customNameDest",
  "netBiosNameSrc", "netBiosNameDst", "dnsNameSrc", "dnsNameDst",
  "src_hostnames", "dest_hostnames",
  "protocol", "app_proto", "proto", "vlan",
  "geo", "countryName", "countryCode",
  "fkSeriale", "response", "ack", "uid", "metaField", "dettaglio",
]);

// Prettify key names: snake_case → "Snake Case"
const prettyKey = (k) =>
  String(k)
    .replace(/[._]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const ScalarValue = ({ value }) => (
  <Typography
    sx={{
      fontSize: 12.5,
      color: colors.grey[100],
      fontFamily: "monospace",
      wordBreak: "break-word",
      lineHeight: 1.5,
    }}
  >
    {String(value)}
  </Typography>
);

// Generic value renderer for nested objects / arrays.
// Uses a tree-style indented layout — no nested boxes-in-boxes.
const ValueRenderer = ({ value, depth = 0 }) => {
  if (isEmpty(value)) return <Typography sx={{ color: colors.grey[600], fontSize: 12 }}>—</Typography>;

  // Array
  if (Array.isArray(value)) {
    // simple array → chips
    if (value.every((v) => typeof v !== "object" || v === null)) {
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
          {value.map((v, i) => (
            <Chip key={i} label={String(v)} size="small"
              sx={{ bgcolor: "#ffffff0d", color: colors.grey[200], fontSize: 11, height: 18, fontFamily: "monospace" }} />
          ))}
        </Box>
      );
    }
    // array of objects → stacked with subtle separator
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {value.map((item, i) => (
          <Box
            key={i}
            sx={{
              pl: 1.2,
              borderLeft: `2px solid ${colors.grey[800]}`,
              "&:hover": { borderLeftColor: "#45909566" },
            }}
          >
            <ValueRenderer value={item} depth={depth + 1} />
          </Box>
        ))}
      </Box>
    );
  }

  // Object
  if (typeof value === "object") {
    const entries = Object.entries(value).filter(([, v]) => !isEmpty(v));
    if (entries.length === 0) return <Typography sx={{ color: colors.grey[600], fontSize: 12 }}>—</Typography>;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
        {entries.map(([k, v]) => {
          const isComplex = v && typeof v === "object";
          if (isComplex) {
            // header + indented children (rail on the left)
            return (
              <Box key={k} sx={{ mt: 0.4 }}>
                <Typography
                  sx={{
                    color: "#459095",
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mb: 0.3,
                  }}
                >
                  {prettyKey(k)}
                </Typography>
                <Box sx={{ pl: 1.2, borderLeft: `2px solid ${colors.grey[800]}` }}>
                  <ValueRenderer value={v} depth={depth + 1} />
                </Box>
              </Box>
            );
          }
          // simple key/value row — flexible label column, no rigid grid
          return (
            <Box
              key={k}
              sx={{
                display: "flex",
                gap: 1.2,
                alignItems: "baseline",
                py: 0.3,
              }}
            >
              <Typography
                sx={{
                  color: colors.grey[400],
                  fontSize: 11.5,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                  fontWeight: 500,
                  flex: "0 0 auto",
                  minWidth: 0,
                  maxWidth: 180,
                  whiteSpace: "nowrap",
                }}
              >
                {prettyKey(k)}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ScalarValue value={v} />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return <ScalarValue value={value} />;
};

// Flatten a nested object/array into dotted-key entries.
// Example: { http: { hostname: "x", url: "/y" } } → [["http.hostname","x"], ["http.url","/y"]]
const flattenObject = (obj, prefix = "") => {
  const out = [];
  if (obj == null) return out;
  if (Array.isArray(obj)) {
    // primitive array → join as single value; object array → recurse with [idx]
    if (obj.every((v) => typeof v !== "object" || v === null)) {
      if (obj.length > 0) out.push([prefix, obj.map(String).join(", ")]);
      return out;
    }
    obj.forEach((item, i) => out.push(...flattenObject(item, `${prefix}[${i}]`)));
    return out;
  }
  if (typeof obj === "object") {
    Object.entries(obj).forEach(([k, v]) => {
      if (isEmpty(v)) return;
      const next = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === "object") out.push(...flattenObject(v, next));
      else out.push([next, v]);
    });
    return out;
  }
  out.push([prefix, obj]);
  return out;
};

// Render a flattened key with the prefix dimmed and the leaf brighter
const DottedLabel = ({ path }) => {
  const parts = path.split(".");
  const leaf = parts.pop();
  return (
    <Box component="span" sx={{
      fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.3,
      fontWeight: 500, color: colors.grey[400],
    }}>
      {parts.length > 0 && (
        <Box component="span" sx={{ color: colors.grey[600] }}>
          {parts.map(prettyKey).join(" › ")}{" › "}
        </Box>
      )}
      {prettyKey(leaf)}
    </Box>
  );
};

// ── Endpoint block (Source / Destination) ──────────────────────────────────
// Renders without the outer SidePanelSection wrapper — used inside a 2-column grid
const EndpointBlock = ({
  title, accentIcon, accentColor,
  ip, port, mac,
  hostnames,
  country, countryName,
}) => {
  const hasContent = ip || mac || port ||
    (hostnames && Object.values(hostnames).some(Boolean));
  if (!hasContent) return null;
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
        {accentIcon}
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: accentColor || "#459095",
            textTransform: "uppercase",
            letterSpacing: 0.8,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{
        p: 1.4,
        borderRadius: 1,
        backgroundColor: "#1c1c1c",
        border: `1px solid ${colors.grey[800]}`,
      }}>
        <SidePanelKV label="IP"      value={ip}  mono copyable />
        <SidePanelKV label="Port"    value={port != null && port !== "" ? String(port) : null} mono />
        <SidePanelKV label="MAC"     value={mac} mono copyable />
        {hostnames && (
          <>
            <SidePanelKV label="DNS"     value={hostnames.dns_name}     mono copyable />
            <SidePanelKV label="NetBIOS" value={hostnames.netbios_name} mono copyable />
            <SidePanelKV label="Custom"  value={hostnames.custom_name} />
          </>
        )}
        {country && (
          <Box sx={{
            display: "grid",
            gridTemplateColumns: "100px 1fr",
            gap: 1.2,
            py: 0.55,
            alignItems: "center",
          }}>
            <Typography sx={{
              fontSize: 11.5, color: colors.grey[400], textTransform: "uppercase",
              letterSpacing: 0.4, fontWeight: 500,
            }}>
              Country
            </Typography>
            <Tooltip title={countryName || country}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.8 }}>
                <Flag code={country} style={{ width: 20, height: 14, borderRadius: 2 }} />
                <Typography sx={{ fontSize: 12.5, color: colors.grey[100], fontWeight: 500 }}>
                  {countryName || country}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Tires-IA explanation (compact, under Endpoints) ─────────────────────────
const ExplainSection = ({ state, onExplain, canExplain }) => {
  const [dots, setDots] = useState(0);
  const loading = state.status === "loading";
  const done = state.status === "done";

  useEffect(() => {
    if (!loading) { setDots(0); return undefined; }
    const id = setInterval(() => setDots((d) => (d + 1) % 4), 500);
    return () => clearInterval(id);
  }, [loading]);

  let body;
  if (loading) {
    body = `Tires-IA is analyzing${".".repeat(dots)}`;
  } else if (done) {
    body = state.text;
  } else if (state.status === "error") {
    body = "Unable to get an explanation right now.";
  } else {
    body = "Get a plain-language description of this detection from Tires-IA.";
  }
  const linkLabel = done ? "Regenerate" : state.status === "error" ? "Retry" : "Explain this detection";

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{
        p: 1.6,
        borderRadius: 1,
        backgroundColor: "#45909514",
        border: "1px solid #45909555",
        overflow: "hidden",
        position: "relative",
      }}>
        {loading && (
          <LinearProgress
            sx={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              backgroundColor: "#45909533",
              "& .MuiLinearProgress-bar": { backgroundColor: "#5fb0b5" },
            }}
          />
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.8 }}>
          <AutoAwesomeIcon sx={{ fontSize: 16, color: "#5fb0b5" }} />
          <Typography sx={{
            fontSize: 11.5, fontWeight: 700, color: "#5fb0b5",
            textTransform: "uppercase", letterSpacing: 0.8,
          }}>
            Tires-IA Analysis
          </Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, py: 0.8 }}>
            <CircularProgress size={20} thickness={5} sx={{ color: "#5fb0b5" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#8fd0d4" }}>
              {body}
            </Typography>
          </Box>
        ) : (
        <Typography
          className={done ? "tiresia-chat" : undefined}
          sx={{
            fontSize: 13,
            lineHeight: 1.6,
            color: done ? colors.grey[100] : colors.grey[300],
            fontStyle: done ? "normal" : "italic",
            maxHeight: 200,
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {body}
        </Typography>
        )}
        {!loading && canExplain && (
          <Box
            component="button"
            type="button"
            onClick={onExplain}
            sx={{
              mt: 0.8, p: 0, border: "none", background: "none", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 0.5,
              fontSize: 12.5, fontWeight: 600, color: "#5fb0b5", fontFamily: "inherit",
              textDecoration: "underline", textUnderlineOffset: 3,
              "&:hover": { color: "#8fd0d4" },
              "&:focus-visible": { outline: "1px solid #459095", outlineOffset: 2 },
            }}
          >
            <SearchIcon sx={{ fontSize: 15 }} />
            {linkLabel}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── main ────────────────────────────────────────────────────────────────────
// `explainable`: show the Tires-IA section (with its Explain link).
// `autoExplain`: request the explanation as soon as the panel opens on a log
// (e.g. from the table's right-click menu) instead of waiting for the button.
const LogSidePanel = ({ log, open, onClose, explainable = false, autoExplain = false }) => {
  const [payload, setPayload]               = useState(null);
  const [payloadLoading, setPayloadLoading] = useState(false);
  const [showRaw, setShowRaw]               = useState(false);
  const [explainState, setExplainState]     = useState({ status: "idle", text: "" });
  const explainReq                          = useRef(0);

  const runExplain = useCallback((target) => {
    const message = target?.signature;
    if (!message) return;
    const reqId = ++explainReq.current;
    setExplainState({ status: "loading", text: "" });
    API.chatBotAI({ message })
      .then((res) => {
        if (reqId !== explainReq.current) return;
        setExplainState(res?.data ? { status: "done", text: String(res.data) } : { status: "error", text: "" });
      })
      .catch(() => {
        if (reqId === explainReq.current) setExplainState({ status: "error", text: "" });
      });
  }, []);

  // New log (or panel closed) → drop any previous/in-flight explanation.
  const logKey = log ? (log.id ?? log._id ?? log.signature_id ?? null) : null;
  useEffect(() => {
    explainReq.current += 1;
    setExplainState({ status: "idle", text: "" });
    if (log && explainable && autoExplain) runExplain(log);
    // keyed on the log identity, not the row object (rows get re-created on refresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logKey, explainable, autoExplain, runExplain]);

  useEffect(() => {
    if (!log?.signature_id) { setPayload(null); return; }
    setPayloadLoading(true);
    API.getPayloadDetails({ code: log.signature_id })
      .then((res) => setPayload(res?.data || null))
      .catch(() => setPayload(null))
      .finally(() => setPayloadLoading(false));
  }, [log?.signature_id]);

  if (!log) {
    return <SidePanel open={open} onClose={onClose} title="Log details" width={780} />;
  }

  // Shared style for header action buttons — keeps Explain + .pcapng consistent
  // and avoids the muddy MUI default-text-button hover behaviour.
  const headerBtnSx = {
    minWidth: 0,
    px: 1.2,
    py: 0.4,
    fontSize: 11,
    fontWeight: 600,
    textTransform: "none",
    lineHeight: 1.4,
    color: "#459095",
    borderColor: "#45909555",
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: "#459095",
      backgroundColor: "#45909522",
      color: "#5fb0b5",
    },
    "&.Mui-focusVisible": { outline: "1px solid #459095", outlineOffset: 1 },
  };

  // Build a 1-packet pcap from the log. Uses the full `packet` field if
  // present, otherwise synthesises IP+TCP/UDP headers around the L7
  // `payload` so Wireshark can decode protocol layers properly.
  const pcapAction = (log.payload || log.packet) ? (
    <Tooltip
      title={
        log.packet
          ? "Download the captured packet as a .pcapng file (with Wireshark-visible detection comment)"
          : "Download a .pcapng — IP/TCP/UDP headers are synthesised from "
            + "the log metadata (only L7 payload is stored)"
      }
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
        onClick={() => {
          const tsMs = log.timestamp || log.timeStamp
            ? new Date(log.timestamp || log.timeStamp).getTime()
            : Date.now();
          // pcap-ng so the file carries a Wireshark-visible comment with
          // the detection metadata for this packet.
          const blob = buildPcapngMulti([log], tsMs);
          if (!blob) return;
          const safeId = String(log.signature_id || log.event_id || "capture")
            .replace(/[^a-zA-Z0-9_-]/g, "_");
          downloadBlob(blob, `detection-${safeId}.pcapng`);
        }}
        sx={headerBtnSx}
      >
        .pcapng
      </Button>
    </Tooltip>
  ) : null;

  const headerActions = (
    <Box sx={{ display: "flex", gap: 0.8, alignItems: "center" }}>
      {pcapAction}
    </Box>
  );

  const title    = log.signature || log.tipoAttacco || log.classificazione || "Log details";
  const ts       = fmtTs(log.timestamp || log.timeStamp);
  const subtitle = [ts, log.signature_id ? `Detection ID ${log.signature_id}` : null].filter(Boolean).join(" · ");

  const srcHostnames = log.src_hostnames || {
    dns_name:     log.dnsNameSrc,
    netbios_name: log.netBiosNameSrc,
    custom_name:  log.customNameSrc || log.hostNameAtt,
  };
  const dstHostnames = log.dest_hostnames || {
    dns_name:     log.dnsNameDst,
    netbios_name: log.netBiosNameDst,
    custom_name:  log.customNameDest || log.hostNameDest,
  };

  const srcCountry   = log.geo?.src?.country_code  || null;
  const srcCountryN  = log.geo?.src?.country_name  || null;
  const destCountry  = log.geo?.dest?.country_code || null;
  const destCountryN = log.geo?.dest?.country_name || null;
  // legacy fallback: log.countryCode/countryName usually refers to src
  const fallbackSrcCountry  = !srcCountry  && log.src_ip  ? log.countryCode : null;
  const fallbackSrcCountryN = !srcCountryN && log.src_ip  ? log.countryName : null;

  const otherSimple = Object.entries(log).filter(
    ([k, v]) => !HANDLED.has(k) && !isEmpty(v) && typeof v !== "object"
  );
  const otherComplex = Object.entries(log).filter(
    ([k, v]) => !HANDLED.has(k) && !isEmpty(v) && typeof v === "object"
  );

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      icon={<ArticleOutlinedIcon sx={{ fontSize: 22 }} />}
      title={title}
      subtitle={subtitle}
      actions={headerActions}
      width={780}
    >
      {/* ── Source ↔ Destination (side by side) ───────────────────── */}
      <SidePanelSection title="Endpoints">
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "stretch", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <EndpointBlock
            title="Source"
            accentIcon={<CallMadeIcon sx={{ fontSize: 16, color: "#ff9966" }} />}
            accentColor="#ff9966"
            ip={log.src_ip}
            port={log.src_port}
            mac={log.src_mac}
            hostnames={srcHostnames}
            country={srcCountry || fallbackSrcCountry}
            countryName={srcCountryN || fallbackSrcCountryN}
          />
          <EndpointBlock
            title="Destination"
            accentIcon={<CallReceivedIcon sx={{ fontSize: 16, color: "#7b8bff" }} />}
            accentColor="#7b8bff"
            ip={log.dest_ip}
            port={log.dest_port}
            mac={log.dest_mac}
            hostnames={dstHostnames}
            country={destCountry}
            countryName={destCountryN}
          />
        </Box>
      </SidePanelSection>

      {/* ── Tires-IA explanation ─────────────────────────────────── */}
      {explainable && <ExplainSection state={explainState} onExplain={() => runExplain(log)} canExplain={Boolean(log.signature)} />}
      {explainable && <TiresiaKnowsSection log={log} />}

      {/* ── Network Info ─────────────────────────────────────────── */}
      {(log.protocol || log.app_proto || log.proto || log.vlan != null) && (
        <SidePanelSection title="Network Info">
          <SidePanelKV label="Protocol"  value={log.protocol || log.proto} />
          <SidePanelKV label="App Proto" value={log.app_proto} />
          <SidePanelKV label="VLAN"      value={log.vlan != null && log.vlan !== "" ? String(log.vlan) : null} />
        </SidePanelSection>
      )}

      {/* ── Detection (core + additional + nested all together) ──── */}
      <SidePanelSection title="Detection">
        <SidePanelKV label="Details"        value={log.signature} />
        <SidePanelKV label="Detection ID"   value={log.signature_id} mono copyable />
        <SidePanelKV label="Classification" value={log.classificazione || log.tipoAttacco} />
        <SidePanelKV label="Timestamp"      value={ts} mono />

        {/* extra flat fields — same KV layout, no extra heading */}
        {otherSimple.map(([k, v]) => (
          <SidePanelKV
            key={k}
            label={prettyKey(k)}
            value={String(v)}
            mono={typeof v !== "string" || /^[0-9a-f.:/_-]+$/i.test(String(v))}
          />
        ))}

        {/* nested objects/arrays — flattened into the same KV stream
            using dotted paths (e.g. "http › hostname"); keeps the UI a
            single uniform list, no separate cards/sub-headers. */}
        {otherComplex.flatMap(([k, v]) => flattenObject(v, k)).map(([path, value], i) => (
          <Box
            key={`${path}-${i}`}
            sx={{
              display: "grid",
              gridTemplateColumns: "180px 1fr",
              gap: 1.2,
              py: 0.55,
              borderBottom: `1px dashed ${colors.grey[800]}`,
              "&:last-of-type": { borderBottom: "none" },
              alignItems: "center",
            }}
          >
            <DottedLabel path={path} />
            <Typography sx={{
              fontSize: 12.5, fontFamily: "monospace", color: colors.grey[100],
              wordBreak: "break-word",
            }}>
              {String(value)}
            </Typography>
          </Box>
        ))}
      </SidePanelSection>

      {/* ── Raw payload info (collapsible) ───────────────────────── */}
      {!payloadLoading && payload && (Array.isArray(payload) ? payload.length > 0 : Object.keys(payload).length > 0) && (
        <Box sx={{ mb: 2 }}>
          <Box
            onClick={() => setShowRaw((s) => !s)}
            sx={{
              display: "flex", alignItems: "center", gap: 0.6, cursor: "pointer", mb: 1, userSelect: "none",
              "&:hover .raw-title": { color: "#5fb0b5" },
            }}
          >
            <ExpandMoreIcon className="raw-title" sx={{
              color: "#459095", fontSize: 18,
              transition: "transform 150ms",
              transform: showRaw ? "rotate(0deg)" : "rotate(-90deg)",
            }} />
            <Typography className="raw-title" sx={{
              fontSize: 11, fontWeight: 700, color: "#459095",
              textTransform: "uppercase", letterSpacing: 0.8,
            }}>
              Raw Payload Info
            </Typography>
          </Box>
          <Collapse in={showRaw}>
            <Box sx={{ pl: 1.2, borderLeft: `2px solid ${colors.grey[800]}` }}>
              <ValueRenderer value={payload} />
            </Box>
          </Collapse>
        </Box>
      )}
    </SidePanel>
  );
};

export default LogSidePanel;
